import { AddProcedureToReportReply, CreateReportRequest, CreateReportWithProcedureInstancesRequest, ExportReportJsonReply, GenerateReportFromTemplateRequest, ProcedureReportRequest, ReadProceduresForReportReply, RemoveProcedureFromReportRequest, ReportReply, ReportsReply, UpdateReportRequest } from "@/backend/express/controllers/reportController";
import { CodedReportTemplatePreviewResponse, CodedReportTemplateProcedureResponse, CodedReportTemplateResponse } from "@/types/runReportTypes";
import { Client } from "./client";
import { FusaReply } from "@/types/replyTypes";
import { ContextLogReadAllResponse } from "@/types/contextLogTypes";
import { ReportType } from "@/types/reportTypes";

export class ReportController {
  client: Client;

  constructor(client?: Client) {
    this.client = client || new Client();
  }

  public async addProcedureToReport(req: ProcedureReportRequest): Promise<AddProcedureToReportReply> {
    return this.client.create(`/api/reports/addProcedureToReport`, req);
  }

  public async removeProcedureFromReport(req: RemoveProcedureFromReportRequest): Promise<FusaReply> {
    return this.client.update(`/api/reports/removeProcedureFromReport`, req);
  }

  public async updateReport(req: UpdateReportRequest): Promise<ReportReply> {
    return this.client.update(`/api/reports`, req);
  }

  public async readReportProcedures(reportId: string): Promise<ReadProceduresForReportReply> {
    return this.client.read(`/api/reports/readProceduresForReport/${reportId}`);
  }

  public async readAllContextLogsForReport(reportId: string): Promise<ContextLogReadAllResponse> {
    return this.client.read(`/api/context-logs/readAllForReport/${reportId}`);
  }

  public async readReport(id: string): Promise<ReportReply> {
    return this.client.read(`/api/reports/${id}`);
  }

  public async deleteReport(id: string): Promise<FusaReply> {
    return this.client.delete(`/api/reports/${id}`);
  }

  public async readAllReports(type: ReportType): Promise<ReportsReply> {
    return this.client.read(`/api/reports/readAllReports/${type}`);
  }

  public async createReport(req: CreateReportRequest): Promise<ReportReply> {
    return this.client.create(`/api/reports`, req);
  }

  public async createReportWithProcedureInstances(req: CreateReportWithProcedureInstancesRequest): Promise<ReportReply> {
    return this.client.create(`/api/reports/createReportWithProcedureInstances`, req);
  }

  public async readReportsBySourceId(sourceId: string): Promise<ReportsReply> {
    return this.client.read(`/api/reports/readAllReportsBySourceId/${sourceId}`);
  }

  public async readReportsForProcedure(procedureId: string): Promise<ReportsReply> {
    // coded templates too: the web app shows both kinds (Veriform Desktop, which cannot generate a coded report, gets legacy ones only)
    return this.client.read(`/api/reports/readReportsForProcedure/${procedureId}?include=coded`);
  }

  public async readAllTestsForTemplate(reportTemplateId: string): Promise<ReportsReply> {
    return this.client.read(`/api/reports/readAllTestsForTemplate/${reportTemplateId}`);
  }

  public async exportJson(reportId: string): Promise<ExportReportJsonReply> {
    return this.client.read(`/api/reports/exportJson/${reportId}`);
  }

  public async readCodedReportTemplate(id: string): Promise<CodedReportTemplateResponse> {
    return this.client.read(`/api/reports/${id}/coded`);
  }

  public async readCodedTemplateProcedure(id: string): Promise<CodedReportTemplateProcedureResponse> {
    return this.client.read(`/api/reports/${id}/coded/procedure`);
  }

  public async readCodedTemplatePreview(id: string, runId: string): Promise<CodedReportTemplatePreviewResponse> {
    return this.client.read(`/api/reports/${id}/coded/preview/${runId}`);
  }

  public async generateReportFromTemplate(request: GenerateReportFromTemplateRequest): Promise<ReportReply> {
    return this.client.create(`/api/reports/generateReportFromTemplate`, request);
  }
}
